import { Record, Map as ImmutableMap, List } from 'immutable';
import AuditEvent, { AuditEventKeys } from 'automation-ui-management/records/AuditHistory/AuditEvent';
import { getAuditEventTypes, getDeletedActionCount } from 'automation-ui-management/records/AuditHistory/utils';
import * as AuditEventTypes from 'automation-ui-management/records/AuditHistory/AuditEventTypes';
import AuditEventActionMetadata from 'automation-ui-management/records/AuditHistory/AuditEventActionMetadata';
import * as AuditEventSubTypes from 'automation-ui-management/records/AuditHistory/AuditEventSubTypes';
const AuditEventHistoryKeys = {
  AUDIT_EVENTS: 'auditEvents',
  REVISIONS: 'revisions'
};
const REVISION_UPDATE_TYPES = {
  CREATE: 'CREATE'
};
const ACTION_MODIFICATION_SUBTYPES = Object.values(AuditEventSubTypes);

// TODO: Revisit this when changes in canvas are merged

export default class AuditEventHistory extends Record({
  [AuditEventHistoryKeys.AUDIT_EVENTS]: [],
  [AuditEventHistoryKeys.REVISIONS]: []
}, 'AuditEventHistory') {
  constructor(props) {
    const propMap = ImmutableMap(props);
    super(propMap);
  }
  getAuditEvents() {
    return this[AuditEventHistoryKeys.AUDIT_EVENTS];
  }
  getFilteredAuditEvents(auditEventHistoryFilters = null) {
    const auditEvents = this.getAuditEvents();
    if (!auditEventHistoryFilters) {
      return auditEvents;
    }
    return auditEvents.filter(auditEvent => !auditEventHistoryFilters.shouldFilterOut(auditEvent));
  }
  getRevisions() {
    return this[AuditEventHistoryKeys.REVISIONS];
  }
  generateAuditEvents(revisions) {
    const auditEvents = [];
    if (revisions.length === 0) {
      return auditEvents;
    }
    if (revisions.length === 1) {
      const revision = revisions[0];
      if (revision.updateType === REVISION_UPDATE_TYPES.CREATE) {
        auditEvents.push(new AuditEvent({
          [AuditEventKeys.AUDIT_EVENT_TYPE]: AuditEventTypes.CREATED_WORKFLOW,
          [AuditEventKeys.ALL_AUDIT_EVENT_TYPES]: List([AuditEventTypes.CREATED_WORKFLOW]),
          [AuditEventKeys.USER_ID]: revision.flow.updateMetadata.updatedBy && revision.flow.updateMetadata.updatedBy.userId,
          [AuditEventKeys.TIMESTAMP]: revision.timestamp,
          [AuditEventKeys.REVISIONS]: [revision]
        }));
      }
      return auditEvents;
    }
    for (let idx = 1; idx < revisions.length; idx++) {
      const nextRevision = revisions[idx - 1];
      const revision = revisions[idx];
      const allAuditEventTypes = getAuditEventTypes(revision, nextRevision);
      let auditEventType = allAuditEventTypes.first();
      let auditEventSubType = null;
      let actionModCountOverride = null;
      if (ACTION_MODIFICATION_SUBTYPES.includes(auditEventType)) {
        auditEventSubType = auditEventType;
        auditEventType = AuditEventTypes.ACTION_MODIFICATION;
        if (auditEventSubType === AuditEventSubTypes.DELETED_ACTION) {
          actionModCountOverride = getDeletedActionCount(revision, nextRevision);
        }
      }
      const userId = nextRevision.flow.updateMetadata.updatedBy && nextRevision.flow.updateMetadata.updatedBy.userId;
      const timestamp = nextRevision.timestamp;
      const associatedBulkEnrollment = nextRevision.associatedBulkEnrollment;
      const enrollExistingDecision = nextRevision.enrollExistingDecision;
      const nextRevisions = [nextRevision, revision];
      const auditEventActionMetadata = new AuditEventActionMetadata({}).updateCountBasedOnEventSubType(auditEventSubType, actionModCountOverride || 1);
      auditEvents.push(new AuditEvent({
        [AuditEventKeys.ASSOCIATED_BULK_ENROLLMENT]: associatedBulkEnrollment,
        [AuditEventKeys.AUDIT_EVENT_TYPE]: auditEventType,
        [AuditEventKeys.ALL_AUDIT_EVENT_TYPES]: allAuditEventTypes,
        [AuditEventKeys.USER_ID]: userId,
        [AuditEventKeys.TIMESTAMP]: timestamp,
        [AuditEventKeys.REVISIONS]: nextRevisions,
        [AuditEventKeys.AUDIT_EVENT_ACTION_METADATA]: auditEventActionMetadata,
        [AuditEventKeys.ENROLL_EXISTING_DECISION]: enrollExistingDecision
      }));
    }
    const mergedAuditEvents = [];
    let currentAuditEvent = null;
    for (let idx = 0; idx < auditEvents.length; idx++) {
      const nextAuditEvent = auditEvents[idx];
      if (currentAuditEvent === null) {
        currentAuditEvent = nextAuditEvent;
      } else {
        if (currentAuditEvent.shouldMergeWith(nextAuditEvent)) {
          currentAuditEvent = currentAuditEvent.mergeAuditEvent(nextAuditEvent);
        } else {
          mergedAuditEvents.push(currentAuditEvent);
          currentAuditEvent = nextAuditEvent;
        }
      }
    }
    if (currentAuditEvent !== null) {
      mergedAuditEvents.push(currentAuditEvent);
    }
    const revision = revisions[revisions.length - 1];
    if (revision.updateType === REVISION_UPDATE_TYPES.CREATE) {
      mergedAuditEvents.push(new AuditEvent({
        [AuditEventKeys.AUDIT_EVENT_TYPE]: AuditEventTypes.CREATED_WORKFLOW,
        [AuditEventKeys.ALL_AUDIT_EVENT_TYPES]: List([AuditEventTypes.CREATED_WORKFLOW]),
        [AuditEventKeys.USER_ID]: revision.flow.updateMetadata.updatedBy && revision.flow.updateMetadata.updatedBy.userId,
        [AuditEventKeys.TIMESTAMP]: revision.timestamp,
        [AuditEventKeys.REVISIONS]: [revision]
      }));
    }
    return mergedAuditEvents;
  }
  updateEvents(revisionsOrWrapped) {
    const revisions = revisionsOrWrapped.map(maybeWrapped => 'toHydratedHybridFlowRevision' in maybeWrapped ? maybeWrapped.toHydratedHybridFlowRevision() : maybeWrapped);
    const newAuditEvents = this.generateAuditEvents(revisions);
    const updatedAuditEventHistory = this.update(AuditEventHistoryKeys.REVISIONS,
    // @ts-expect-error this seems like a bug but leaving to prevent regressions
    prevRevisions => [...prevRevisions, revisions]).update(AuditEventHistoryKeys.AUDIT_EVENTS, prevAuditEvents => [...prevAuditEvents, ...newAuditEvents]);
    return updatedAuditEventHistory;
  }
  clearEvents() {
    return this.update(AuditEventHistoryKeys.REVISIONS, () => []).update(AuditEventHistoryKeys.AUDIT_EVENTS, () => []);
  }
}